import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import ListItemIcon from '@mui/material/ListItemIcon'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { KNAdvancedDropdownOption, KNAdvancedDropdownProps } from './types'

const KNAdvancedDropdown = ({
  label,
  placeholder,
  value,
  options,
  multiple,
  size,
  sx,
  error,
  helperText,
  disabled,
  onChange,
  onDelete,
}: KNAdvancedDropdownProps): ReactElement => {
  const { t } = useTranslation()
  const [selectedOptions, setSelectedOptions] = useState<KNAdvancedDropdownOption[]>([])

  useEffect(() => {
    setSelectedOptions(options.filter((option) => multiple ? value.includes(option.value) : value === option.value))
  }, [options, value, multiple])

  return (
    <FormControl
      size={size}
      fullWidth
      sx={sx}
      error={error}
      disabled={disabled}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        displayEmpty
        label={label}
        multiple={multiple}
        renderValue={(): ReactElement => {
          if (selectedOptions.length === 0 && placeholder) {
            return (
              <Typography variant="p3">{placeholder}</Typography>
            )
          }
          return (
            <>
              {selectedOptions.map((option) => multiple ? (
                <Chip
                  key={option.value}
                  variant="outlined"
                  size="small"
                  label={option.label}
                  onDelete={onDelete ? (_event) => {
                    onDelete(option.value)
                  } : undefined}
                  // prevents opening the dropdown list when clicking on "delete" element
                  onMouseDown={onDelete ? (event) => {
                    if ((event.target instanceof HTMLElement || event.target instanceof SVGElement) && !event.target.classList.contains('MuiChip-label')) {
                      event.preventDefault()
                      event.stopPropagation()
                    }
                  } : undefined}
                />
              ) : (
                <Typography key={option.value} variant="p3">{option.label}</Typography>
              ))}
            </>
          )
        }}
        value={value ?? (multiple ? [] : '')}
        onChange={(event) => {
          if (multiple) {
            if (event.target.value.includes('selectAll')) {
              if (options.length === selectedOptions.length) {
                onChange?.([])
              } else {
                onChange?.(options.map((option) => option.value))
              }
            } else {
              onChange?.(event.target.value as typeof value)
            }
          } else {
            onChange?.(event.target.value as typeof value)
          }
        }}
        fullWidth
      >
        {multiple && (
          <MenuItem value="selectAll">
            <ListItemIcon>
              <Checkbox
                size="small"
                checked={options.length === selectedOptions.length}
                indeterminate={selectedOptions.length > 0 && options.length > selectedOptions.length}
                sx={{ padding: 0 }}
              />
            </ListItemIcon>
            {options.length === selectedOptions.length ? t('general.unselect_all') : t('general.select_all')}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {multiple && (
              <ListItemIcon>
                <CheckIcon
                  sx={{
                    margin: '-2px',
                    opacity: value.includes(option.value) ? 1 : 0.1,
                  }}
                />
              </ListItemIcon>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              {option.icon}
              <Box>{option.label}</Box>
              {!!option.count && <Typography variant="p5">&times;{option.count}</Typography>}
            </Stack>
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

export default KNAdvancedDropdown
